import { useInstance } from '@meraki-internal/react-dependency-injection';
import { AlertPresenter } from '../../app/AlertBinder.tsx';
import { useEffect, useState } from 'react';
import { Page } from '../../components/page/Page.tsx';
import { HistoryViewModel } from '../../app/HistoryViewModel.ts';
import { EditableMissionState } from '../model/EditableMissionState.ts';
import { useSubscription } from '@meraki-internal/state';
import { MissionNotesEventsTracker } from './MissionNotesEventsTracker.ts';
import { WrapWithCardOnLargerScreen } from '../../components/card/WrapWithCardOnLargerScreen.tsx';
import { QuillEditor } from '../../components/editor/QuillEditor.tsx';

const betterError = (error: any) => {
    if (error && error.displayMessage) {
        // this intentionally is trying to improve the message with minimal assumptions
        // eg, if limit changes from 10000 to 10001, it won't be formatted as nicely, but
        // it will still work
        error.displayMessage = error.displayMessage
            .replace('Notes html: String', 'Notes')
            .replace('10000', '10,000')
            .replace('character(s)', 'characters');
    }

    return error;
};

export const MissionNotes: React.FC<{ mission: EditableMissionState }> = ({ mission }) => {
    const history = useInstance(HistoryViewModel);

    useSubscription(() => mission);

    const [notesHtml, setNotesHtml] = useState<string>();
    const [isSaving, setSaving] = useState(false);
    const alert = useInstance(AlertPresenter);
    const tracking = useInstance(MissionNotesEventsTracker);

    useEffect(() => {
        setNotesHtml(mission.state.notesHtml || '');
    }, [mission]);

    if (notesHtml === undefined) {
        return null;
    }

    const isDirty = notesHtml !== (mission.state.notesHtml || '');

    const onSave = isDirty && !isSaving ? async () => {
        try {
            setSaving(true);
            tracking.track({ oldNotesHtml: mission.state.notesHtml || '', newNotesHtml: notesHtml });
            await mission.saveNotesHtml(notesHtml);
        }
        catch (_error) {
            alert.showAndLogError(betterError(_error));
        }
        finally {
            setSaving(false);
        }
    } : undefined;

    const onBack = () => {
        if (!isDirty){
            history.back(mission.pages.details.path());
        } else {
            setNotesHtml(mission.state.notesHtml || '');
        }
    };

    return (
        <Page title="Mission Notes" onBack={onBack} backLabel={isDirty ? 'Cancel' : undefined} actionLabel="Save" onAction={onSave}>
            <WrapWithCardOnLargerScreen>
                <QuillEditor
                    placeholder="Add notes to your mission!"
                    height="calc(100vh - 200px)"
                    html={notesHtml}
                    onChange={html => setNotesHtml(html)}
                    autofocus
                    preventBlur
                />
            </WrapWithCardOnLargerScreen>
        </Page>
    );
};
